import config from 'config';
import { z } from 'zod';
import { createAPIError } from 'utils/exceptions/apiError';

import type {
  ChallengeIdentity,
  ChallengeResponse,
  CreateChallengeParams,
  GetVerdictResponse,
} from './types';

/**
 * The OTP service refused to create an Email-validation challenge because of a
 * recent one (a Challenge block). The Partner can ask again at `expiresAt`.
 */
export class OtpRateLimitedError extends Error {
  name = 'OtpRateLimitedError';

  expiresAt: Date;

  constructor(expiresAt: Date) {
    super(`OTP challenge rate limited until ${expiresAt.toISOString()}`);
    this.expiresAt = expiresAt;
  }
}

/**
 * The only field of the BFF's 429 body that the SSU reads. The short and the
 * long Challenge block look the same.
 */
const rateLimitedBodySchema = z.object({
  details: z.object({
    data: z.object({ expiresAt: z.string().datetime({ offset: true }) }),
  }),
});

/**
 * Creates an Email-validation challenge, and replaces any challenge that is
 * still pending for the same email and discriminator. The create sends no
 * email: the OTP iframe sends the code when it first loads the hash.
 *
 * Takes no abort signal on purpose: leaving the page must not cancel a request
 * that may already have replaced the pending challenge.
 *
 * @throws {OtpRateLimitedError} on a 429 that carries `details.data.expiresAt`.
 * @throws An API error on any other failure, including a 429 without it.
 */
export async function createChallenge(
  variables: CreateChallengeParams,
): Promise<ChallengeResponse> {
  const response = await fetch(
    `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v1/otp-challenge`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(variables),
    },
  );

  if (response.status === 429) {
    const body = rateLimitedBodySchema.safeParse(
      await response
        .clone()
        .json()
        .catch(() => undefined),
    );

    if (body.success) {
      throw new OtpRateLimitedError(new Date(body.data.details.data.expiresAt));
    }
  }

  if (!response.ok) {
    throw await createAPIError(response);
  }

  return (await response.json()) satisfies ChallengeResponse;
}

/**
 * Resumes the pending Email-validation challenge of an email in this browser, found through the Discriminator. Sends no email. Returns nothing when none is pending: the BFF never returns a challenge that was confirmed, revoked, expired or failed.
 *
 * Takes no abort signal, like {@link createChallenge}: leaving the page must not stop the resume and the create that follows it halfway.
 *
 * @throws An API error on any failure.
 */
export async function resumeChallenge(
  identity: ChallengeIdentity,
): Promise<ChallengeResponse | undefined> {
  const response = await fetch(
    `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v1/otp-challenge/pending`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(identity),
    },
  );

  if (response.status === 204) {
    return undefined;
  }

  if (!response.ok) {
    throw await createAPIError(response);
  }

  return (await response.json()) satisfies ChallengeResponse;
}

/**
 * Reads the Verdict of an Email-validation challenge. The hash goes in the
 * `Authorization` header, and nothing else is sent: with a valid hash the OTP
 * service ignores any other identifier, and with an invalid one it would answer
 * for another record (BFF ADR 0011).
 *
 * @throws An API error on any failure, including the service's `NOT_FOUND`
 * once it has purged the record.
 */
export async function getVerdict(hash: string): Promise<GetVerdictResponse> {
  const response = await fetch(
    `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v1/otp-confirm`,
    { headers: { Authorization: `Bearer ${hash}` } },
  );

  if (!response.ok) {
    throw await createAPIError(response);
  }

  return (await response.json()) satisfies GetVerdictResponse;
}
